import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import PopupService from "./popups/popup-service";
import H5 from "./Titles/h5";
import Link from "./Links/link";
import Helper from "../services/helper.service";

import { useSubdomain } from "../contexts/subdomain";
import { getServices } from "../services/api.service";
import Loading from "./Loading/loading";
import Hr from "./Style/hr";

const toFinitePrice = (value) => {
    if (value === undefined || value === null || value === "") {
        return null;
    }

    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : null;
};

const buildServicePricing = (localService, subdomainService) => {
    const regularPrice = toFinitePrice(localService?.price) ?? 0;

    const configuredSubdomainPrice =
        toFinitePrice(subdomainService?.customPrice) ??
        toFinitePrice(subdomainService?.price);

    const subdomainPrice = configuredSubdomainPrice ?? regularPrice;

    const apiPriceLoaded = Boolean(
        subdomainService?.apiPrice &&
        subdomainService.apiPrice.realPrice !== undefined &&
        subdomainService.apiPrice.realPrice !== null &&
        !subdomainService.apiPriceFallbackUsed
    );

    const apiUserPrice = apiPriceLoaded
        ? toFinitePrice(subdomainService.apiPrice.realPrice)
        : null;

    const effectivePrice =
        apiUserPrice ??
        configuredSubdomainPrice ??
        regularPrice;

    return {
        regularPrice,
        subdomainPrice,
        apiUserPrice,
        effectivePrice,
        apiPriceLoaded,
        hasSubdomainPrice:
            configuredSubdomainPrice !== null &&
            configuredSubdomainPrice !== regularPrice,
        hasApiPrice:
            apiUserPrice !== null &&
            apiUserPrice !== subdomainPrice,
        pricingSource:
            apiUserPrice !== null
                ? "api"
                : configuredSubdomainPrice !== null
                    ? "subdomain"
                    : "regular",
    };
};

const ServicesSelection = ({ previouslySelectedServices = [], returnedData }) => {
    const { t, i18n } = useTranslation();

    const [listOfServices, setServices] = useState([]);
    const [loading, setLoading] = useState(true);

    const { subdomainData, loadingSubdomain } = useSubdomain();

    const [filteredServices, setFilteredServices] = useState([]);
    const [selectedServices, setSelectedServices] = useState(previouslySelectedServices);
    const [popupContent, setPopupContent] = useState(null);
    const [isPopupOpen, setIsPopupOpen] = useState(false);

    const [urlServiceId, setUrlServiceId] = useState(null);
    const [showAllServices, setShowAllServices] = useState(false);

    const filterForNoSubdomain = (services) =>
        services.filter((s) => s.enable === true);

    const filterForSubdomain = (services) =>
        services.filter((s) => s.statutProduct !== "unavailable");

    const getServiceIdFromUrl = () => {
        const params = new URLSearchParams(window.location.search);

        return (
            params.get("serviceId") ||
            params.get("serviceid") ||
            params.get("service") ||
            null
        );
    };


    const hasAnyPreferentialPrice = (service) =>
        Boolean(
            service?.hasSubdomainPrice ||
            service?.hasApiPrice ||
            service?.pricingSource === "api"
        );

    // Only one previous price is displayed:
    // - API price: cross out the subdomain price.
    // - Subdomain price: cross out the regular catalogue price.
    const getReferencePrice = (service) => {
        if (!hasAnyPreferentialPrice(service)) {
            return null;
        }

        const effectivePrice = toFinitePrice(service?.price) ?? 0;

        if (service?.pricingSource === "api") {
            const subdomainPrice = toFinitePrice(service?.subdomainPrice);

            if (subdomainPrice !== null && subdomainPrice > effectivePrice) {
                return subdomainPrice;
            }
        }

        const regularPrice = toFinitePrice(service?.regularPrice);

        return regularPrice !== null && regularPrice > effectivePrice
            ? regularPrice
            : null;
    };

    useEffect(() => {
        const serviceIdFromUrl = getServiceIdFromUrl();

        if (serviceIdFromUrl) {
            setUrlServiceId(serviceIdFromUrl);
            setShowAllServices(false);
        }
    }, []);

    useEffect(() => {
        getServices()
            .then((response) => {
                if (response?.status) {
                    setServices(response.data);
                } else {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                    returnedData({ step: "error" });
                }

                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
                window.scrollTo({ top: 0, behavior: "smooth" });
                returnedData({ step: "error" });
            });
    }, [returnedData]);

    useEffect(() => {
        if (loadingSubdomain) return;

        if (!Array.isArray(listOfServices) || listOfServices.length === 0) {
            setFilteredServices([]);
            return;
        }

        if (subdomainData && Array.isArray(subdomainData.services) && subdomainData.services.length > 0) {
            const subServiceIds = subdomainData.services.map((s) => String(s.serviceId));

            const subdomainListed = listOfServices
                .filter((service) => subServiceIds.includes(String(service._id)))
                .map((service) => {
                    const custom = subdomainData.services.find(
                        (s) => String(s.serviceId) === String(service._id)
                    );

                    const pricing = buildServicePricing(service, custom);

                    return {
                        ...service,

                        serviceId: service._id,

                        // Final amount used by the next steps and payment.
                        price: pricing.effectivePrice,

                        // Keep every price level separately.
                        regularPrice: pricing.regularPrice,
                        subdomainPrice: pricing.subdomainPrice,
                        apiUserPrice: pricing.apiUserPrice,
                        effectivePrice: pricing.effectivePrice,
                        pricingSource: pricing.pricingSource,
                        hasSubdomainPrice: pricing.hasSubdomainPrice,
                        hasApiPrice: pricing.hasApiPrice,

                        // Kept for compatibility with existing components.
                        fallbackPrice: pricing.regularPrice,
                        customPrice: custom?.customPrice ?? custom?.price ?? null,

                        endpointId: custom?.endpointId || "",

                        apiPrice: custom?.apiPrice || null,
                        apiPriceLoaded: pricing.apiPriceLoaded,
                        apiPriceFallbackUsed: custom?.apiPriceFallbackUsed || false,
                        priceDetails: custom?.priceDetails || null,
                    };
                });

            const filtered = filterForSubdomain(subdomainListed);

            if (filtered.length === 0) {
                const fallback = filterForSubdomain(listOfServices).map((service) => {
                    const regularPrice = toFinitePrice(service.price) ?? 0;

                    return {
                        ...service,
                        serviceId: service._id,
                        price: regularPrice,
                        regularPrice,
                        subdomainPrice: regularPrice,
                        apiUserPrice: null,
                        effectivePrice: regularPrice,
                        pricingSource: "regular",
                        hasSubdomainPrice: false,
                        hasApiPrice: false,
                        fallbackPrice: regularPrice,
                        apiPriceLoaded: false,
                        apiPriceFallbackUsed: true,
                    };
                });

                setFilteredServices(fallback);
            } else {
                setFilteredServices(filtered);

                if (filtered.length === 1) {
                    setSelectedServices([filtered[0]._id]);
                }
            }
        } else {
            const enabledOnly = filterForNoSubdomain(listOfServices).map((service) => {
                const regularPrice = toFinitePrice(service.price) ?? 0;

                return {
                    ...service,
                    serviceId: service._id,
                    price: regularPrice,
                    regularPrice,
                    subdomainPrice: regularPrice,
                    apiUserPrice: null,
                    effectivePrice: regularPrice,
                    pricingSource: "regular",
                    hasSubdomainPrice: false,
                    hasApiPrice: false,
                    fallbackPrice: regularPrice,
                    apiPriceLoaded: false,
                    apiPriceFallbackUsed: false,
                };
            });

            setFilteredServices(enabledOnly);
        }
    }, [subdomainData, loadingSubdomain, listOfServices, i18n.language]);

    useEffect(() => {
        if (loading || loadingSubdomain) return;
        if (!Array.isArray(filteredServices) || filteredServices.length === 0) return;
        if (!urlServiceId) return;

        const matchedService = filteredServices.find((service) => {
            return (
                String(service._id) === String(urlServiceId) ||
                String(service.serviceId) === String(urlServiceId)
            );
        });

        if (!matchedService) return;

        setSelectedServices([matchedService._id]);
    }, [filteredServices, loading, loadingSubdomain, urlServiceId]);

    const hasPartnerOrApiPricing = filteredServices.some(hasAnyPreferentialPrice);

    const displayedServices = React.useMemo(() => {
        if (!urlServiceId || showAllServices) {
            return filteredServices;
        }

        return filteredServices.filter((service) => {
            return (
                String(service._id) === String(urlServiceId) ||
                String(service.serviceId) === String(urlServiceId)
            );
        });
    }, [filteredServices, urlServiceId, showAllServices]);

    const toggleServiceSelection = (serviceId) => {
        setSelectedServices((prevSelected) => {
            if (prevSelected.includes(serviceId)) {
                return prevSelected.filter((id) => id !== serviceId);
            }

            return [...prevSelected, serviceId];
        });
    };

    const openPopup = (title, content) => {
        setPopupContent({ title, content });
        setIsPopupOpen(true);
    };

    const closePopup = () => {
        setIsPopupOpen(false);
        setPopupContent(null);
    };

    const calculateSelectedTotals = () => {
        return selectedServices.reduce(
            (totals, serviceId) => {
                const service = filteredServices.find((item) => item._id === serviceId);

                if (!service) {
                    return totals;
                }

                const finalPrice = Number(service.price ?? 0);
                const referencePrice = getReferencePrice(service);

                totals.final += finalPrice;
                totals.reference += referencePrice ?? finalPrice;

                if (referencePrice !== null && referencePrice > finalPrice) {
                    totals.hasPreferentialPrice = true;
                }

                return totals;
            },
            {
                reference: 0,
                final: 0,
                hasPreferentialPrice: false,
            }
        );
    };

    const selectedTotals = calculateSelectedTotals();

    const handleNextStep = () => {
        if (selectedServices.length > 0) {
            const selectedServiceDetails = filteredServices.filter((service) =>
                selectedServices.includes(service._id)
            );

            window.scrollTo({ top: 0, behavior: "smooth" });

            returnedData({
                step: "step2",
                services: selectedServiceDetails,
            });

            return;
        }

        return Helper.toast("Veuillez sélectionner au moins une prestation.", "error");
    };

    const isGlobalLoading = loading || loadingSubdomain;

    return (
        <div className="w-full mx-auto mb-6 px-4 py-6">
            <h2 className="text-base font-bold mb-4 text-main">
                {t("services.title")}
            </h2>

            <div className="flex justify-between border-b pb-2 mb-4">
                <p className="font-medium">{t("services.productInfo")}</p>
                <p className="font-medium">{t("services.price")}</p>
            </div>

            <div className="space-y-4">
                {isGlobalLoading ? (
                    <div>
                        <div className="py-12">
                            <Loading />
                        </div>
                        <div className="text-center text-gray-500 py-4">
                            {t("services.loading")}
                        </div>
                        <Hr />
                    </div>
                ) : (
                    displayedServices.map((service) => {
                        const currentDescription =
                            service.description?.[i18n.language] ||
                            service.description?.fr ||
                            "";

                        const referencePrice = getReferencePrice(service);

                        const hasCurrentDescription =
                            typeof currentDescription === "string" &&
                            currentDescription.replace(/<[^>]*>/g, "").trim().length > 0;

                        const title =
                            service.title?.[i18n.language] ||
                            service.title?.fr ||
                            t("services.untitled");

                        const shortDescription = (
                            service.shortDescription?.[i18n.language] ||
                            service.shortDescription?.fr ||
                            ""
                        ).replace(/<[^>]*>/g, "");

                        return (
                            <div
                                key={service._id}
                                className={`p-4 rounded-md shadow-sm border ${selectedServices.includes(service._id)
                                    ? "border-primary bg-blue-50"
                                    : "border-gray-200"
                                    } flex justify-between items-center gap-4 md:gap-12 cursor-pointer`}
                                onClick={() => toggleServiceSelection(service._id)}
                            >
                                <div className="flex items-start space-x-4">
                                    <input
                                        type="checkbox"
                                        checked={selectedServices.includes(service._id)}
                                        onClick={(event) => event.stopPropagation()}
                                        onChange={() => toggleServiceSelection(service._id)}
                                        className="accent-primary mt-1 h-5 text-primary border-gray-300 rounded focus:ring-primary"
                                    />

                                    <div>
                                        <h3 className="font-semibold text-lg text-main">
                                            {title}
                                        </h3>

                                        <p className="text-sm text-gray-600">
                                            {shortDescription}
                                        </p>

                                        {referencePrice !== null && (
                                            <span className="text-xs font-medium text-primary block mt-1">
                                                {t("services.preferentialPrice", {
                                                    partner: subdomainData?.fullName || "",
                                                })}
                                            </span>
                                        )}

                                        {hasCurrentDescription && (
                                            <button
                                                type="button"
                                                onClick={(event) => {
                                                    event.stopPropagation();

                                                    openPopup(
                                                        title,
                                                        currentDescription
                                                    );
                                                }}
                                                className="text-primary text-sm mt-2 inline-block relative group transform transition-transform ease-out duration-300"
                                            >
                                                {t("services.learnMore")}

                                                <span className="absolute bottom-0 left-0 w-full h-[1px] bg-primary scale-x-0 group-hover:scale-x-100 transform transition-transform ease-out duration-300 origin-left" />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className="flex min-w-[180px] flex-col items-end whitespace-nowrap text-right">
                                    {referencePrice !== null ? (
                                        <>
                                            <span className="text-sm text-gray-400 line-through">
                                                {referencePrice.toFixed(2)}&nbsp;€
                                            </span>

                                            <span className="text-lg font-bold text-primary">
                                                {Number(service.price || 0).toFixed(2)}&nbsp;€
                                            </span>
                                        </>
                                    ) : (
                                        <span className="text-lg font-semibold text-main">
                                            {Number(service.price || 0).toFixed(2)}&nbsp;€
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {urlServiceId &&
                !showAllServices &&
                filteredServices.length > displayedServices.length && (
                    <div className="mt-4 text-left">
                        <button
                            type="button"
                            onClick={() => setShowAllServices(true)}
                            className="text-sm text-primary hover:underline hover:text-main transition"
                        >
                            Voir tous les services
                        </button>
                    </div>
                )}

            <div className="flex justify-between flex-col md:flex-row items-center gap-0 md:gap-12">
                {!hasPartnerOrApiPricing ? (
                    <div className="mt-8">
                        <H5 title="Vous possédez un compte Le Club By ?" theme="main" />
                        <p className="text-sm text-gray-600">
                            {t("services.loginSuggestion")}
                        </p>
                        <Link
                            link="https://app.leclubby.com"
                            text={t("services.login")}
                            textColor="primary"
                            externalLink={true}
                        />
                    </div>
                ) : (
                    <div></div>
                )}

                <div className="mt-8 flex justify-between items-center gap-6 text-main">
                    <p className="text-xl font-bold">{t("services.total")}</p>

                    <div className="flex flex-col items-end text-right">
                        <span className={`text-xl font-bold ${selectedTotals.hasPreferentialPrice
                            ? "text-primary"
                            : "text-main"
                            }`}>
                            {selectedTotals.final.toFixed(2)} €
                        </span>
                    </div>
                </div>
            </div>

            <div className="mt-4 text-right">
                <button
                    className={`px-6 py-2 rounded-md ${selectedServices.length > 0
                        ? "bg-main text-white hover:bg-primary"
                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                        }`}
                    onClick={handleNextStep}
                    disabled={selectedServices.length === 0}
                >
                    {t("services.nextStep")}
                </button>
            </div>

            <PopupService
                isOpen={isPopupOpen}
                onClose={closePopup}
                content={popupContent}
            />
        </div>
    );
};

export default ServicesSelection;